import { useContext, useState } from 'react';

import { Box, Table, Tbody, Thead } from '@chakra-ui/react';

import { Deal } from '../../gql/dealGql';
import {
  FollowUpStatusEnum,
  SortDirection,
  StructuringFollowUpDealsDocument,
  StructuringFollowUpDealsQuery,
  StructuringFollowUpSortColumn,
  useOnFollowUpUpdateSubscription,
  useOnStructuringFollowUpUpdateSubscription,
  useStructuringFollowUpDealsQuery,
} from '../../gql/generated/graphql';

import Pagination from '../Pagination';

import StructuringFollowUpTableHeader from './StructuringFollowUpTableHeader';
import StructuringFollowUpTableRow from './StructuringFollowUpTableRow';
import { TABLE_COLUMNS_ENTRIES } from './columnConfig';

import { PermissionEnum } from '../../constants/permissions';
import usePaginationControls from '../../hooks/usePaginationControls';
import { AbilityContext, FiltersContext } from '../../libs/contextLib';

export type StructuringFollowUpDeals = NonNullable<
  NonNullable<StructuringFollowUpDealsQuery['structuringFollowUpDeals']>['deals']
>;

type SortStructuringFollowUpDealsProps = {
  deals: Deal[];
  sortColumn: StructuringFollowUpSortColumn;
  sortDirection: SortDirection;
};

const sortStructuringFollowUpDeals = ({
  deals,
  sortColumn,
  sortDirection,
}: SortStructuringFollowUpDealsProps) => {
  return deals.sort((a, b) => {
    const aIsDue = a.follow_up?.status === FollowUpStatusEnum.Due;
    const bIsDue = b.follow_up?.status === FollowUpStatusEnum.Due;

    // If both have due follow-ups, sort by follow_up.date_utc
    if (aIsDue && bIsDue) {
      const dateA = new Date(a.follow_up?.date_utc ?? '');
      const dateB = new Date(b.follow_up?.date_utc ?? '');
      return dateA.getTime() - dateB.getTime();
    }

    // If only one has a due follow-up, it should be first
    if (aIsDue) {
      return -1;
    }
    if (bIsDue) {
      return 1;
    }

    let comparison = 0;
    switch (sortColumn) {
      case StructuringFollowUpSortColumn.Customer:
        comparison = (a.customer?.first_name ?? '').localeCompare(b.customer?.first_name ?? '');
        break;
      case StructuringFollowUpSortColumn.DealState:
        comparison = (a.state ?? '').localeCompare(b.state ?? '');
        break;
      case StructuringFollowUpSortColumn.Advisor:
        comparison = (a.advisor?.name ?? '').localeCompare(b.advisor?.name ?? '');
        break;
      case StructuringFollowUpSortColumn.StructuringManager:
        comparison = (a.structuringManager?.name ?? '').localeCompare(
          b.structuringManager?.name ?? '',
        );
        break;
      case StructuringFollowUpSortColumn.Date:
      default: {
        const dateA = new Date(a.structuring_follow_up?.created_at ?? '');
        const dateB = new Date(b.structuring_follow_up?.created_at ?? '');
        comparison = dateB.getTime() - dateA.getTime();
        break;
      }
    }

    return sortDirection === SortDirection.Asc ? comparison : -comparison;
  });
};

const StructuringFollowUpsTable = () => {
  const abilities = useContext(AbilityContext);
  const {
    filters: {
      global: {
        selectedPodIds,
        sources,
        types,
        showOnlyUnread,
        showAssignedToMe,
        showBootRequested,
        showActiveTags,
        showScheduledFollowUps,
      },
    },
  } = useContext(FiltersContext);

  const paginationControls = usePaginationControls();
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.Desc);
  const [sortColumn, setSortColumn] = useState<StructuringFollowUpSortColumn>(
    StructuringFollowUpSortColumn.Date,
  );

  const assignedToMe = abilities.has(PermissionEnum.FilterDealOwner) ? true : showAssignedToMe;

  const structuringQueryVariables = {
    sortColumn,
    sortDirection,
    page: paginationControls.currentPage,
    pageSize: paginationControls.itemsPerPage,
    sources,
    dealTypes: types,
    assignedToMe,
    bootRequested: showBootRequested,
    notifications: showOnlyUnread,
    podIds: selectedPodIds,
    showActiveTags,
    showScheduledFollowUps,
  };
  const structuringQueryDocAndVariables = {
    query: StructuringFollowUpDealsDocument,
    variables: structuringQueryVariables,
  };

  const { data: structuringFollowUpDealsData } = useStructuringFollowUpDealsQuery({
    skip: !sources.length,
    variables: structuringQueryVariables,
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      paginationControls.setTotalRecords(data.structuringFollowUpDeals?.totalRecords ?? 0);
    },
  });

  // Updates to the details of the structuring follow up
  useOnStructuringFollowUpUpdateSubscription({
    skip: !sources.length,
    variables: { sources, types, assignedToMe, podIds: selectedPodIds },
    onData: ({ client, data }) => {
      const updatedDeals = (data?.data?.onStructuringFollowUpUpdate ?? []) as Deal[];
      if (updatedDeals.length) {
        client.cache.updateQuery(structuringQueryDocAndVariables, (existingData) => {
          if (!existingData?.structuringFollowUpDeals?.deals) {
            return null;
          }
          const existingDeals = existingData.structuringFollowUpDeals.deals;
          const newDeals = updatedDeals.reduce<Deal[]>((acc, updatedDeal) => {
            if (!updatedDeal?.id || !updatedDeal.structuring_follow_up) {
              return acc;
            }

            return [...acc, updatedDeal];
          }, []);

          // Combine all deals first, then sort
          const combinedDeals = sortStructuringFollowUpDeals({
            deals: [...existingDeals, ...newDeals],
            sortColumn,
            sortDirection,
          });

          return {
            ...existingData,
            structuringFollowUpDeals: { deals: combinedDeals },
          };
        });
      }
    },
  });

  // Updates to the follow up (appointment/reminder)
  useOnFollowUpUpdateSubscription({
    skip: !sources.length,
    variables: {
      sources,
      types,
      scheduledByMeFilter: abilities.has(PermissionEnum.FilterDealOwner),
    },
    onData: ({ client, data }) => {
      const updatedDeals = (data?.data?.onFollowUpUpdate ?? []) as Deal[];
      if (updatedDeals.length) {
        client.cache.updateQuery(structuringQueryDocAndVariables, (existingData) => {
          if (!existingData?.structuringFollowUpDeals?.deals) {
            return null;
          }
          const existingDeals = existingData.structuringFollowUpDeals.deals;

          // Filter out deals that are being updated
          const updatedDealsList = existingDeals.filter(
            (deal: Deal) => !updatedDeals.some((updatedDeal) => updatedDeal?.id === deal?.id),
          );

          // Process deals to update
          const dealsToUpdate = updatedDeals.reduce<Deal[]>((acc, updatedDeal) => {
            if (
              !updatedDeal?.id ||
              updatedDeal?.follow_up?.status === FollowUpStatusEnum.Scheduled
            ) {
              return acc;
            }

            return [...acc, updatedDeal];
          }, []);

          const combinedDeals = sortStructuringFollowUpDeals({
            deals: [...updatedDealsList, ...dealsToUpdate],
            sortColumn,
            sortDirection,
          });

          return {
            ...existingData,
            structuringFollowUpDeals: {
              ...existingData.structuringFollowUpDeals,
              deals: combinedDeals,
            },
          };
        });
      }
    },
  });

  return (
    <Box w="100%">
      <Box p={6} bgColor="oxfordBlue" borderTopRadius={8} w="100%" />
      <Box w="100%" overflowX="auto">
        <Table>
          <Thead cursor="pointer" color="white" bgColor="queenBlue" w="100%" px={6} height="55px">
            {TABLE_COLUMNS_ENTRIES.map(([header, { sortColumn: columnName, hideHeader }]) =>
              columnName ? (
                <StructuringFollowUpTableHeader
                  key={header}
                  header={header}
                  hideHeader={hideHeader}
                  canSort
                  columnName={columnName}
                  sortColumn={sortColumn}
                  setSortColumn={setSortColumn}
                  sortDirection={sortDirection}
                  setSortDirection={setSortDirection}
                />
              ) : (
                <StructuringFollowUpTableHeader
                  key={header}
                  header={header}
                  hideHeader={hideHeader}
                />
              ),
            )}
          </Thead>
          <Tbody>
            {structuringFollowUpDealsData?.structuringFollowUpDeals?.deals?.map((deal) =>
              deal ? <StructuringFollowUpTableRow key={deal.id} deal={deal} /> : null,
            )}
          </Tbody>
        </Table>
      </Box>
      <Pagination
        paginationControls={paginationControls}
        borderTop="1px solid"
        borderColor="lightGray"
      />
    </Box>
  );
};

export default StructuringFollowUpsTable;
